import { Box, Grid, Stack } from '@mui/material';
import { t } from 'i18next';
import { isEmpty } from 'lodash';
import moment from 'moment';
import { BaseController, BaseState } from '../../../common/controllers/BaseController';
import { OverviewType, getLabel as getOverviewTypeLabel } from '../../../common/dashboards/enums/OverviewType';
import { TrendDataScale } from '../../../common/dashboards/models/TrendDataScale';
import { DateRange } from '../../../common/dashboards/types';
import { TabChart } from '../../../common/inventory/components/TabChart';
import { TitleChart } from '../../../common/inventory/components/TitleChart';
import { InventoryDataExtractor } from '../../../common/inventory/extractors/InventoryDataExtractor';
import { InventoryRecap } from '../../../common/inventory/models/InventoryRecap';
import { InventoryService } from '../../../common/inventory/services/InventoryService';
import { getHelpTextMap } from '../../../common/inventory/utils';
import { QueryParamManager } from '../../../common/managers/QueryParamManager';
import { Preset } from '../../../common/models/valueObjects/Preset';
import { bind } from '../../../common/utils/decorators';
import { TestValueWrapper } from '../../../common/utils/TestValueWrapper';
import { UrlHelper } from '../../../common/utils/UrlHelper';
import { DashboardExportDrawer } from '../../../exports/triggers/drawers/DashboardExportDrawer';
import { DashboardExportState } from '../../../exports/triggers/states/DashboardExportState';
import { SecondaryNav } from '../../../layout/components/SecondaryNav';
import { EXTERNAL_URLS } from '../../../routing/routes';
import { AdvancedDashboardFilters } from '../components/filters/AdvancedDashboardFilters';
import { formatDateRangeLabel } from '../components/filters/advancedFilters/DateRangePicker';
import { TrendsOverview } from '../components/overview/TrendsOverview';
import { PriorityFixes } from '../components/priorityFixes/PriorityFixes';
import { QueryFilterMapper } from '../mappers/QueryFilterMapper';
import { OrganizationData } from '../models/OrganizationData';
import { OrganizationSummary as OrganizationSummaryModel } from '../models/OrganizationSummary';
import { PriorityFix } from '../models/PriorityFix';
import { DashboardDataProvider } from '../providers/DashboardDataProvider';
import { OrganizationDataProvider } from '../providers/OrganizationDataProvider';
import { formatRange } from '../utils';

type Props = {};

type State = BaseState & {
  initialLoad: boolean;
  loadingData: boolean;

  overviewTypes: Preset[];
  tags: Preset[];
  profiles: Preset[];
  projects: Preset[];
  dateRange: DateRange;
  dateRangeLabel: string;
  exceptions: Preset[]; // state holder for dashboard filter "Include exceptions"
};

export class OrganizationDashboardController extends BaseController<Props, State> {
  private static readonly FILTER_KEY = 'filter';

  private readonly repository = new InventoryService();
  private readonly dashboardDataProvider = new DashboardDataProvider();
  private readonly inventoryDataExtractor = new InventoryDataExtractor();
  private readonly organizationDataProvider = new OrganizationDataProvider();

  private readonly queryParamManager = new QueryParamManager();
  private readonly queryFilterMapper = new QueryFilterMapper();

  private readonly availableExceptions: Preset[];
  private readonly availableOverviewTypes: Preset[];

  private readonly defaultDateRange: DateRange;
  private readonly defaultDateRangeLabel: string;

  private inventory: InventoryRecap | null;
  private organizationSummary: OrganizationSummaryModel | null;
  private priorityFixes: PriorityFix[] | null;

  private exportState: DashboardExportState;

  constructor(props: Props, context: unknown) {
    super(props, context);

    this.inventory = null;
    this.organizationSummary = null;
    this.priorityFixes = null;
    this.exportState = DashboardExportState.getInstance();

    this.availableExceptions = [new Preset('1', t('advancedFilters.includeExceptions'))];
    this.availableOverviewTypes = [
      new Preset(OverviewType.APPLICATIONS, getOverviewTypeLabel(OverviewType.APPLICATIONS)),
      new Preset(OverviewType.FILE_SYSTEMS, getOverviewTypeLabel(OverviewType.FILE_SYSTEMS)),
      new Preset(OverviewType.PKCS11_TOKENS, getOverviewTypeLabel(OverviewType.PKCS11_TOKENS)),
      new Preset(OverviewType.NETWORK_ANALYZER, getOverviewTypeLabel(OverviewType.NETWORK_ANALYZER)),
    ];

    this.defaultDateRange = [moment().subtract(7, 'days').startOf('day'), moment().endOf('day')] as DateRange;
    this.defaultDateRangeLabel = t('advancedFilters.shortcuts.past7Days');

    // Setup base state
    const {
      tags = [],
      profiles = [],
      projects = [],
      exceptions = [],
      overviewTypes = [...this.availableOverviewTypes],
      dateRange = this.defaultDateRange,
      dateRangeLabel = this.defaultDateRangeLabel,
    } = this.queryFilterMapper.parseQueryValue(this.queryParamManager.extractQueryValue(OrganizationDashboardController.FILTER_KEY));

    this.state = {
      ...super.getBaseState(),
      initialLoad: true,
      loadingData: true,

      exceptions,
      overviewTypes,
      tags,
      profiles,
      projects,
      dateRange,
      dateRangeLabel,
    };
  }

  componentDidMount() {
    super.componentDidMount();

    this.fetchData();
  }

  componentDidUpdate(prevProps: Readonly<Props>, prevState: Readonly<State>) {
    super.componentDidUpdate(prevProps, prevState);

    const keys = ['tags', 'projects', 'profiles', 'exceptions', 'overviewTypes'] as const;
    const areFiltersChanged = keys.some((key) => {
      const prevStateIds = prevState[key].map((p) => p.getId());
      const currStateIds = this.state[key].map((p) => p.getId());

      return prevStateIds.join(',') !== currStateIds.join(',');
    });

    const previousDateRange = prevState.dateRange.map((item) => item?.format() || 'null').join('-');
    const currentDateRange = this.state.dateRange.map((item) => item?.format() || 'null').join('-');
    const isDateRangeChanged = previousDateRange !== currentDateRange;
    const isDateRangeLabelChanged = prevState.dateRangeLabel !== this.state.dateRangeLabel;

    if (areFiltersChanged || isDateRangeChanged) {
      this.fetchData();
    }

    if (areFiltersChanged || isDateRangeChanged || isDateRangeLabelChanged) {
      const { tags, projects, profiles, exceptions, overviewTypes, dateRange, dateRangeLabel } = this.state;
      const isFilterEmpty = isEmpty(tags) && isEmpty(projects) && isEmpty(profiles) && isEmpty(exceptions);
      const areDefaultValues =
        dateRange === this.defaultDateRange && dateRangeLabel === this.defaultDateRangeLabel && overviewTypes.length === this.availableOverviewTypes.length;
      let filters = null;
      if (!isFilterEmpty || !areDefaultValues) {
        filters = this.queryFilterMapper.mapFilters({
          tags,
          projects,
          profiles,
          exceptions,
          overviewTypes,
          dateRange,
          dateRangeLabel,
        });
      }
      this.queryParamManager.updateQueryValue(OrganizationDashboardController.FILTER_KEY, filters);
    }
  }

  componentWillUnmount() {
    super.componentWillUnmount();

    this.exportState.reset();
    this.repository.cancelAllRequests();
    this.dashboardDataProvider.cancelAllRequests();
    this.organizationDataProvider.cancelAllRequests();
  }

  resetFilters() {
    this.setState({
      exceptions: [],
      overviewTypes: [...this.availableOverviewTypes],
      tags: [],
      profiles: [],
      projects: [],
      dateRange: this.defaultDateRange,
      dateRangeLabel: this.defaultDateRangeLabel,
    });
  }

  async fetchData() {
    try {
      this.setState({ loadingData: true });
      this.exportState.reset();

      const tagIds = this.state.tags.map((tag) => tag.getId());
      const projectIds = this.state.projects.map((project) => project.getId());
      const profileIds = this.state.profiles.map((profile) => profile.getId());
      const includeExceptions = this.state.exceptions.length > 0;
      const types = this.state.overviewTypes.map((type) => type.getId());

      const data = await this.dashboardDataProvider.getTrendsOrganizationData(tagIds, projectIds, profileIds, this.state.dateRange, includeExceptions, types);

      this.organizationSummary = data.getSummary();
      this.inventory = data.getInventory();
      this.priorityFixes = data.getPriorityFixes();

      const hasInventory = !data.getInventory().isEmpty();
      const hasPriorityFixes = data.getPriorityFixes().length > 0;
      const hasTrends = data.getSummary().getTrendData().length > 0;

      const canExport = (hasInventory || hasPriorityFixes) && hasTrends;

      this.exportState.setState({
        name: this.organizationSummary.getName(),
        profiles: this.state.profiles,
        projects: this.state.projects,
        tags: tagIds,
        overviews: this.state.overviewTypes.map((type) => type.getId()),
        trends: this.organizationSummary.getTrendData(),
        withExceptions: this.state.exceptions.length > 0,
        dateRange: this.state.dateRange,
        ready: canExport,
      });

      this.setState({ loadingData: false, initialLoad: false });
    } catch (e: unknown) {
      // If error, remove loading state and display an empty one.
      const data = OrganizationData.getBlank();

      this.organizationSummary = data.getSummary();
      this.inventory = data.getInventory();
      this.priorityFixes = data.getPriorityFixes();

      this.setState({ loadingData: false, initialLoad: false });

      this.handleError(e);
    }
  }

  render() {
    const { loadingData, initialLoad } = this.state;

    const { keyStores, hashFunctions, mac, symmetricAlgorithms, asymmetricAlgorithms, operations, keys } = this.inventoryDataExtractor.extractInventoryElements(
      this.inventory
    );

    const summaryTitle = formatRange(this.state.dateRange, TrendDataScale.DAY);
    const organizationCreatedAt = this.organizationSummary?.getCreatedAt()?.toISOString() ?? undefined;

    return this.renderBase(
      <SecondaryNav
        title={this.organizationSummary?.getName() ?? ''}
        rightSection={
          // FIXME: handle out-of-lifecycle signal updates in tests
          TestValueWrapper.wrap(<DashboardExportDrawer onError={this.handleError} />, null)
        }
        getBreadcrumbs={this.getBreadcrumbs}
        loading={this.organizationSummary === null}
      >
        <AdvancedDashboardFilters
          availableExceptions={this.availableExceptions}
          availableOverviewTypes={this.availableOverviewTypes}
          profiles={this.state.profiles}
          projects={this.state.projects}
          tags={this.state.tags}
          overviewTypes={this.state.overviewTypes}
          minDate={organizationCreatedAt}
          exceptions={this.state.exceptions}
          dateRange={this.state.dateRange}
          dateRangeLabel={this.state.dateRangeLabel}
          fetchTags={(query, exclude, limit) => this.organizationDataProvider.getTags(query, exclude, limit)}
          fetchProjects={(query, exclude, limit) => this.organizationDataProvider.getProjects(query, exclude, limit)}
          fetchProfiles={(query, exclude, limit) => this.organizationDataProvider.getProfiles(query, exclude, limit)}
          onUpdateDateRange={({ range, label }) =>
            this.setState({
              dateRange: range,
              dateRangeLabel: label,
            })
          }
          onUpdateExceptions={(exceptions) => this.setState({ exceptions })}
          onUpdateTags={(tags) => this.setState({ tags })}
          onUpdateProjects={(projects) => this.setState({ projects })}
          onUpdateProfiles={(profiles) => this.setState({ profiles })}
          onUpdateOverviewTypes={(overviewTypes) => this.setState({ overviewTypes })}
          onResetFilters={() => this.resetFilters()}
          initialLoad={initialLoad}
          loading={loadingData}
        />

        <Box display="grid" gridTemplateColumns="repeat(12, 1fr)" gridTemplateRows="repeat(3, auto)" gap={2}>
          <Box gridColumn={{ xl: 'span 9', xs: 'span 12' }} gridRow="1 / 1">
            <TrendsOverview
              trends={this.organizationSummary?.getTrendData() || []}
              loading={loadingData}
              initialLoad={initialLoad}
              summaryTitle={summaryTitle}
              onUpdateDateRange={(dateRange) =>
                this.setState({
                  dateRange,
                  dateRangeLabel: formatDateRangeLabel(dateRange),
                })
              }
            />
          </Box>
          <Box gridColumn={{ lg: 'span 9', xs: 'span 12' }} gridRow="2 / 2">
            <Grid container spacing={2}>
              <Grid item lg={8} xs={12}>
                <Stack direction="column" spacing={2}>
                  <TabChart title={t('keys')} categories={keys} helpMessages={getHelpTextMap()} loading={loadingData} initialLoad={initialLoad} />
                  <TitleChart
                    category={operations}
                    title={t('operations')}
                    helpText={t('helpTexts.operations')}
                    loading={loadingData}
                    initialLoad={initialLoad}
                  />
                  <TitleChart
                    category={symmetricAlgorithms}
                    title={t('symmetricAlgorithms')}
                    helpText={t('helpTexts.symmetricAlgorithms')}
                    loading={loadingData}
                    initialLoad={initialLoad}
                  />
                  <TitleChart
                    category={asymmetricAlgorithms}
                    title={t('asymmetricAlgorithms')}
                    helpText={t('helpTexts.asymmetricAlgorithms')}
                    loading={loadingData}
                    initialLoad={initialLoad}
                  />
                </Stack>
              </Grid>
              <Grid item lg={4} xs={12}>
                <Stack direction="column" spacing={2}>
                  <TitleChart
                    category={keyStores}
                    title={t('keyStore')}
                    helpText={t('helpTexts.keyStores')}
                    loading={loadingData}
                    initialLoad={initialLoad}
                    linkTo={UrlHelper.wrapKnowledgeBaseUrl(EXTERNAL_URLS.DOCS_KB_JAVA_KEYSTORES)}
                  />
                  <TitleChart
                    category={hashFunctions}
                    title={t('hashFunctions')}
                    loading={loadingData}
                    initialLoad={initialLoad}
                    helpText={t('helpTexts.hashFunctions')}
                    linkTo={UrlHelper.wrapKnowledgeBaseUrl(EXTERNAL_URLS.DOCS_KB_JAVA_HASH_FUNCTIONS)}
                  />
                  <TitleChart category={mac} title={t('mac')} helpText={t('helpTexts.mac')} loading={loadingData} initialLoad={initialLoad} />
                </Stack>
              </Grid>
            </Grid>
          </Box>
          <Box gridColumn={{ lg: 'span 3', xs: 'span 12' }} gridRow={{ xl: '1 / 3', lg: '2 / 3', xs: '3 / 3' }}>
            <PriorityFixes fixes={this.priorityFixes} loading={loadingData} initialLoad={initialLoad} />
          </Box>
        </Box>
      </SecondaryNav>
    );
  }

  @bind
  private getBreadcrumbs() {
    return this.breadcrumbFactory.getDashboardBreadcrumbs();
  }
}
